import React, { useEffect, useMemo, useState } from 'react';
import { authStorage } from '../../utils/storage';
import PageHead from '../general/PageHead';
import { FileText, Download, Eye, RefreshCw, ChevronRight, ChevronUp, AlertCircle, FileSpreadsheet, Users, Layers, Database } from 'lucide-react';

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultEndDate = toDateInputValue(new Date());
const startSeed = new Date();
startSeed.setDate(startSeed.getDate() - 6);
const defaultStartDate = toDateInputValue(startSeed);

const REPORT_CONFIG = {
  leadership: {
    label: 'Leadership Directory',
    icon: <Users size={18} />,
    endpoint: '/api/admin/reports/leadership',
    description: 'Current officer assignments by bargaining unit',
    group: 'leadership',
    supportsUnitScope: true
  },
  leadership_gaps: {
    label: 'Missing Leadership Roles',
    icon: <AlertCircle size={18} />,
    endpoint: '/api/admin/reports/leadership-gaps',
    description: 'Units missing current critical officer coverage',
    group: 'leadership',
    supportsUnitScope: false
  },
  leadership_mismatches: {
    label: 'Legacy vs Current Mismatches',
    icon: <AlertCircle size={18} />,
    endpoint: '/api/admin/reports/leadership-mismatches',
    description: 'Differences between legacy fields and officer model',
    group: 'leadership',
    supportsUnitScope: false
  },
  units: {
    label: 'Bargaining Unit Directory',
    icon: <Layers size={18} />,
    endpoint: '/api/admin/reports/units',
    description: 'Master unit list with contract date fields',
    group: 'reference',
    supportsUnitScope: false
  },
  documents: {
    label: 'Document Inventory',
    icon: <FileText size={18} />,
    endpoint: '/api/admin/reports/documents',
    description: 'Uploaded files and metadata by unit',
    group: 'reference',
    supportsUnitScope: true
  },
  audit: {
    label: 'System Change Log',
    icon: <FileSpreadsheet size={18} />,
    endpoint: '/api/admin/reports/audit',
    description: 'Detailed INSERT/UPDATE/DELETE and auth event history',
    group: 'engagement',
    supportsUnitScope: true
  },
  user_activity: {
    label: 'User Login + Movement',
    icon: <Database size={18} />,
    endpoint: '/api/admin/reports/user-activity',
    description: 'Who logged in and how much data movement each user made',
    group: 'engagement',
    supportsUnitScope: false,
    supportsDateRange: true
  }
};

const REPORT_GROUPS = [
  { key: 'engagement', label: 'Engagement & Activity' },
  { key: 'leadership', label: 'Leadership Health' },
  { key: 'reference', label: 'Reference Data' }
];

export default function ReportsAdmin() {
  const token = authStorage.getToken();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('all'); // 'all' | 'unit'
  const [unitId, setUnitId] = useState('');
  const [dataset, setDataset] = useState('leadership');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [preview, setPreview] = useState({ headers: [], rows: [] });
  const [previewing, setPreviewing] = useState(false);
  const [iuExportingCurrent, setIuExportingCurrent] = useState(false);
  const [iuExportingBaseline, setIuExportingBaseline] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [reportUsers, setReportUsers] = useState([]);
  const [reportUsersLoading, setReportUsersLoading] = useState(false);
  const [collapsedCards, setCollapsedCards] = useState({
    runSettings: false,
    templateExports: false
  });
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetailError, setUserDetailError] = useState(null);
  const [drilldownUserId, setDrilldownUserId] = useState(null);

  useEffect(() => {
    async function loadUnits() {
      try {
        setLoading(true);
        const res = await fetch('/api/bargaining-units', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setUnits(Array.isArray(data) ? data : []);
      } catch (e) {
        setError('Failed to load units');
        setUnits([]);
      } finally {
        setLoading(false);
      }
    }
    if (token) loadUnits();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    async function loadReportUsers() {
      if (dataset !== 'user_activity' || !token) return;
      setReportUsersLoading(true);
      try {
        const directRes = await fetch('/api/admin/reports/users?includeInactive=true', {
          headers: { Authorization: `Bearer ${token}` }
        });

        let users = [];
        if (directRes.ok) {
          const payload = await directRes.json();
          users = Array.isArray(payload?.rows) ? payload.rows : [];
        } else {
          // Backward-compatible fallback for servers that do not yet expose /admin/reports/users.
          const params = new URLSearchParams();
          if (startDate) params.set('startDate', startDate);
          if (endDate) params.set('endDate', endDate);
          params.set('includeInactive', 'true');
          const fallbackRes = await fetch(`/api/admin/reports/user-activity?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!fallbackRes.ok) throw new Error(`HTTP ${fallbackRes.status}`);
          const fallbackPayload = await fallbackRes.json();
          users = Array.isArray(fallbackPayload?.rows)
            ? fallbackPayload.rows.map((row) => ({
                user_id: row.user_id,
                username: row.username,
                email: row.email,
                full_name: row.full_name,
                role: row.role,
                status: row.status
              }))
            : [];
        }

        const normalized = users
          .filter((u) => u && u.user_id !== undefined && u.user_id !== null)
          .sort((a, b) => {
            const aName = String(a.full_name || a.username || a.email || '').toLowerCase();
            const bName = String(b.full_name || b.username || b.email || '').toLowerCase();
            return aName.localeCompare(bName);
          });

        if (!cancelled) {
          setReportUsers(normalized);
          setSelectedUserId((prev) => (
            prev !== 'all' && !normalized.some((u) => String(u.user_id) === String(prev))
              ? 'all'
              : prev
          ));
        }
      } catch (e) {
        if (!cancelled) setReportUsers([]);
      } finally {
        if (!cancelled) setReportUsersLoading(false);
      }
    }

    loadReportUsers();
    return () => { cancelled = true; };
  }, [dataset, token, startDate, endDate]);

  useEffect(() => {
    if (dataset === 'user_activity') {
      setUserDetail(null);
      setUserDetailError(null);
      setDrilldownUserId(null);
    }
  }, [dataset, selectedUserId]);

  const currentConfig = REPORT_CONFIG[dataset];
  const groupedReports = useMemo(() => {
    const entries = Object.entries(REPORT_CONFIG);
    return REPORT_GROUPS.map((group) => ({
      ...group,
      reports: entries.filter(([, config]) => config.group === group.key)
    }));
  }, []);

  const canRun = useMemo(() => {
    if (currentConfig.supportsUnitScope && scope === 'unit') return !!unitId;
    if (currentConfig.supportsDateRange && startDate && endDate) {
      return startDate <= endDate;
    }
    return true;
  }, [currentConfig, scope, unitId, startDate, endDate]);

  async function runPreview() {
    setPreviewing(true);
    setError(null);
    try {
      if (currentConfig.supportsDateRange && startDate && endDate && startDate > endDate) {
        setError('Start date must be before end date.');
        setPreview({ headers: [], rows: [] });
        return;
      }

      const params = new URLSearchParams();
      if (currentConfig.supportsUnitScope && scope === 'unit' && unitId) {
        params.set('unitId', unitId);
      }
      if (dataset === 'user_activity' && selectedUserId !== 'all') {
        params.set('userId', selectedUserId);
      }
      if (currentConfig.supportsDateRange) {
        params.set('startDate', startDate);
        params.set('endDate', endDate);
      }

      const res = await fetch(`${currentConfig.endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setPreview(json || { headers: [], rows: [] });
    } catch (e) {
      setError('Failed to run preview. Please try again.');
      setPreview({ headers: [], rows: [] });
    } finally {
      setPreviewing(false);
    }
  }

  function handleReportSelect(nextDataset) {
    const nextConfig = REPORT_CONFIG[nextDataset];
    setDataset(nextDataset);
    setError(null);
    setPreview({ headers: [], rows: [] });
    setUserDetail(null);
    setUserDetailError(null);
    if (nextDataset !== 'user_activity') {
      setSelectedUserId('all');
    }
    if (!nextConfig.supportsUnitScope) {
      setScope('all');
      setUnitId('');
    }
  }

  function toggleCard(cardKey) {
    setCollapsedCards((prev) => ({ ...prev, [cardKey]: !prev[cardKey] }));
  }

  async function loadUserActivityHistory(row) {
    if (!row || !row.user_id) return;
    const nextUserId = String(row.user_id);
    const currentUserId = String(userDetail?.user?.user_id || '');
    if (nextUserId !== currentUserId) {
      setUserDetail(null);
    }
    setDrilldownUserId(nextUserId);
    setUserDetailLoading(true);
    setUserDetailError(null);

    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await fetch(`/api/admin/reports/user-activity/${encodeURIComponent(row.user_id)}/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        // Backward-compatible fallback when server has not yet restarted with history route.
        if (res.status === 404) {
          const auditRes = await fetch('/api/admin/reports/audit', {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!auditRes.ok) {
            throw new Error(`Failed to load history (audit fallback HTTP ${auditRes.status})`);
          }
          const auditPayload = await auditRes.json();
          const auditRows = Array.isArray(auditPayload?.rows) ? auditPayload.rows : [];

          const userKeys = [row.username, row.email]
            .map((v) => String(v || '').trim().toLowerCase())
            .filter(Boolean);
          const matched = auditRows
            .filter((entry) => userKeys.includes(String(entry.changed_by || '').trim().toLowerCase()))
            .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
            .slice(0, 500);

          const loginOps = new Set([
            'LOGIN',
            'LOGIN_SUCCESS',
            'LOGIN_CODE_SUCCESS',
            'LOGOUT',
            'LOGIN_FAILED',
            'LOGIN_TOKEN_REQUESTED',
            'LOGIN_TOKEN_INVALID',
            'LOGIN_TOKEN_REUSE',
            'LOGIN_TOKEN_EXPIRED',
            'LOGIN_CODE_INVALID',
            'LOGIN_CODE_REUSE',
            'LOGIN_CODE_EXPIRED'
          ]);
          const insertOps = new Set(['INSERT', 'CREATE']);
          const updateOps = new Set(['UPDATE', 'ASSIGN_EMPLOYEES']);
          const deleteOps = new Set(['DELETE']);

          let loginEventCount = 0;
          let insertsCount = 0;
          let updatesCount = 0;
          let deletesCount = 0;
          matched.forEach((entry) => {
            const op = String(entry.operation || '').toUpperCase();
            if (loginOps.has(op)) loginEventCount += 1;
            if (insertOps.has(op)) insertsCount += 1;
            if (updateOps.has(op)) updatesCount += 1;
            if (deleteOps.has(op)) deletesCount += 1;
          });

          setUserDetail({
            rows: matched,
            summary: {
              total_events_count: matched.length,
              login_event_count: loginEventCount,
              inserts_count: insertsCount,
              updates_count: updatesCount,
              deletes_count: deletesCount,
              movement_count: insertsCount + updatesCount + deletesCount,
              last_activity_in_range: matched.length ? matched[0].timestamp : null
            },
            user: {
              user_id: row.user_id,
              username: row.username,
              email: row.email,
              full_name: row.full_name,
              role: row.role,
              status: row.status
            }
          });
          return;
        }

        let message = `Failed to load user activity history (HTTP ${res.status}).`;
        try {
          const errPayload = await res.json();
          if (errPayload?.message) message = errPayload.message;
        } catch (_) {
          // keep default message
        }
        throw new Error(message);
      }
      const json = await res.json();
      setUserDetail(json);
    } catch (e) {
      setUserDetail(null);
      setUserDetailError(e?.message || 'Failed to load user activity history.');
    } finally {
      setUserDetailLoading(false);
    }
  }

  function formatDateTime(value) {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return String(value);
    }
  }

  async function exportCsv() {
    if (currentConfig.supportsDateRange && startDate && endDate && startDate > endDate) {
      setError('Start date must be before end date.');
      return;
    }

    const params = new URLSearchParams();
    if (currentConfig.supportsUnitScope && scope === 'unit' && unitId) {
      params.set('unitId', unitId);
    }
    if (dataset === 'user_activity' && selectedUserId !== 'all') {
      params.set('userId', selectedUserId);
    }
    if (currentConfig.supportsDateRange) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    }
    params.set('format', 'csv');

    try {
      const res = await fetch(`${currentConfig.endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/csv' }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      const fname = `${dataset}_${scope === 'unit' && unitId ? unitId : 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError('Failed to export CSV');
    }
  }

  async function exportIuOfficers(endpoint, filenameSuffix, setLoading) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      const fname = `CN4_AllOfficers_Vacancies_${filenameSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError('Failed to export IU officers');
    } finally {
      setLoading(false);
    }
  }

  function formatFieldName(key) {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^bu /i, '')
      .trim()
      .split(/\s+/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  function renderCell(header, value, row) {
    if (value === null || value === undefined) return <span className="text-muted">-</span>;

    const identityHeaders = ['full_name', 'name', 'username', 'email'];
    const isIdentityCell = identityHeaders.includes(header);

    if (isIdentityCell) {
      if (dataset === 'user_activity' && row?.user_id) {
        return (
          <button
            type="button"
            className="table-inline-link"
            onClick={() => loadUserActivityHistory(row)}
          >
            {String(value)}
          </button>
        );
      }
      return <span className="table-inline-identity">{String(value)}</span>;
    }

    if (header === 'did_login') {
      return String(value).toLowerCase() === 'yes' ? 'Yes' : 'No';
    }

    // Date formatting
    if (['timestamp', 'created_at', 'updated_at'].includes(header)) {
      try {
        return new Date(value).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
        });
      } catch (e) { return String(value); }
    }

    // Audit Changes
    if (['changedData', 'differences'].includes(header)) {
      let data = value;
      if (typeof value === 'string') {
        try { data = JSON.parse(value); } catch (e) { return value; }
      }
      if (!data || typeof data !== 'object') return <span className="text-muted">No changes</span>;

      // Handle { old, new } structure
      const oldData = data.old || {};
      const newData = data.new || {};

      // If flat object (legacy), just show keys
      if (!data.old && !data.new) {
        return <div className="code-block">{JSON.stringify(data).slice(0, 50)}...</div>;
      }

      const changes = [];
      const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

      allKeys.forEach(key => {
        const oldVal = oldData[key];
        const newVal = newData[key];
        if (oldVal == null && newVal == null) return;
        if (String(oldVal) === String(newVal)) return;

        changes.push({
          label: formatFieldName(key),
          oldVal,
          newVal
        });
      });

      if (changes.length === 0) return <span className="text-muted">No changes</span>;

      return (
        <div className="changes-list">
          {changes.slice(0, 3).map((c, i) => (
            <div key={i} className="change-item">
              <span className="change-label">{c.label}:</span>
              <span className="change-val old">{c.oldVal ?? 'empty'}</span>
              <span className="change-arrow">→</span>
              <span className="change-val new">{c.newVal ?? 'empty'}</span>
            </div>
          ))}
          {changes.length > 3 && <div className="change-more">+{changes.length - 3} more</div>}
        </div>
      );
    }

    // Default
    return String(value);
  }

  return (
    <>
      <PageHead title="Leadership & Activity" sub="Leadership directory, gaps, engagement, and officer exports" />

      {/* Mobile-only: collapse the fixed two-column layout (inline grid can't be
          overridden by the shared datahouse.css breakpoint) and keep colSpan
          drilldown/empty cells as full-width blocks in stacked-card mode. */}
      <style>{`
        @media (max-width: 900px) {
          .reports-layout { grid-template-columns: 1fr !important; }
          table.tbl td[colspan] { display: block !important; }
          table.tbl td[colspan]::before { content: none !important; }
        }
      `}</style>

      <div className="reports-layout" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start' }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Run Settings */}
          <div className="card">
            <div className="card-head">
              <Eye size={15} style={{ color: 'var(--fg-3)' }} />
              <h3>Run settings</h3>
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => toggleCard('runSettings')}>
                {collapsedCards.runSettings ? <ChevronRight size={13} /> : <ChevronUp size={13} />}
              </button>
            </div>
            {!collapsedCards.runSettings && (
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                <div className="dh-field">
                  <label className="dh-label">Report</label>
                  <select className="dh-select" value={dataset} onChange={e => handleReportSelect(e.target.value)}>
                    {groupedReports.map(group => (
                      <optgroup key={group.key} label={group.label}>
                        {group.reports.map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div style={{ fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 }}>
                  {currentConfig.description}
                </div>

                {dataset === 'user_activity' ? (
                  <div className="dh-field">
                    <label className="dh-label">Employee</label>
                    <select
                      className="dh-select"
                      value={selectedUserId}
                      onChange={e => setSelectedUserId(e.target.value)}
                      disabled={reportUsersLoading}
                    >
                      <option value="all">All Employees</option>
                      {reportUsers.map((u) => {
                        const displayName = u.full_name || u.username || u.email || `User ${u.user_id}`;
                        const secondary = u.username && u.email
                          ? `${u.username} • ${u.email}`
                          : (u.username || u.email || '');
                        return (
                          <option key={u.user_id} value={u.user_id}>
                            {secondary ? `${displayName} (${secondary})` : displayName}
                          </option>
                        );
                      })}
                    </select>
                    <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                      {reportUsersLoading ? 'Loading employees…' : 'Filter to one employee or run across all.'}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="dh-field">
                      <label className="dh-label">Scope</label>
                      <select className="dh-select" value={scope} onChange={e => setScope(e.target.value)}>
                        <option value="all">All Units</option>
                        <option value="unit" disabled={!currentConfig.supportsUnitScope}>Specific Unit</option>
                      </select>
                    </div>

                    {scope === 'unit' && currentConfig.supportsUnitScope && (
                      <div className="dh-field">
                        <label className="dh-label">Bargaining Unit</label>
                        <select className="dh-select" value={unitId} onChange={e => setUnitId(e.target.value)} disabled={loading}>
                          <option value="">Select a unit…</option>
                          {units.map(u => (
                            <option key={u.id} value={u.id}>
                              {`${String(u.LOCAL).padStart(4, '0')}-${String(u.UNIT).padStart(4, '0')} • ${u.NAME}`}
                            </option>
                          ))}
                        </select>
                        {loading && <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Loading units…</span>}
                      </div>
                    )}
                  </>
                )}

                {currentConfig.supportsDateRange && (
                  <>
                    <div className="dh-field">
                      <label className="dh-label">Start Date</label>
                      <input type="date" className="dh-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div className="dh-field">
                      <label className="dh-label">End Date</label>
                      <input type="date" className="dh-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                      <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Default is the most recent 7-day window.</span>
                    </div>
                  </>
                )}

                <div className="list-toolbar" style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!canRun || previewing}
                    onClick={runPreview}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {previewing ? <RefreshCw size={14} className="spin" /> : <Eye size={14} />}
                    Preview
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={!canRun}
                    onClick={exportCsv}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Download size={14} />
                    Export CSV
                  </button>
                </div>

              </div>
            )}
          </div>

          {/* Template Exports */}
          <div className="card">
            <div className="card-head">
              <FileSpreadsheet size={15} style={{ color: 'var(--fg-3)' }} />
              <h3>Template Exports</h3>
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => toggleCard('templateExports')}>
                {collapsedCards.templateExports ? <ChevronRight size={13} /> : <ChevronUp size={13} />}
              </button>
            </div>
            {!collapsedCards.templateExports && (
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 }}>
                  IU officer spreadsheets are exported separately from analytics reports.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => exportIuOfficers('/api/admin/reports/iu-officers/current', 'current', setIuExportingCurrent)}
                    disabled={iuExportingCurrent}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                  >
                    <Download size={14} />
                    {iuExportingCurrent ? 'Exporting…' : 'IU Officers: Current'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => exportIuOfficers('/api/admin/reports/iu-officers/baseline', 'baseline', setIuExportingBaseline)}
                    disabled={iuExportingBaseline}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                  >
                    <Download size={14} />
                    {iuExportingBaseline ? 'Exporting…' : 'IU Officers: Baseline'}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Right: Preview */}
        <div className="card">
          <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Eye size={15} style={{ color: 'var(--fg-3)' }} />
            <h3>{currentConfig.label}</h3>
            {preview?.rows?.length > 0 && (
              <span className="badge" style={{ marginLeft: 4 }}>{preview.rows.length} rows</span>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--fg-3)' }}>{currentConfig.description}</span>
          </div>

          {error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-danger)' }}>
              <AlertCircle size={22} style={{ display: 'block', margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontSize: 13.5 }}>{error}</p>
            </div>
          ) : previewing ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--fg-3)' }}>
              <RefreshCw size={22} className="spin" style={{ display: 'block', margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontSize: 13.5 }}>Generating preview…</p>
            </div>
          ) : preview && preview.headers && preview.headers.length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      {dataset === 'user_activity' && <th>History</th>}
                      {preview.headers.map(h => <th key={h}>{h.replace(/_/g, ' ')}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.rows || []).slice(0, 50).flatMap((r, i) => {
                      const rowUserId = String(r?.user_id || '');
                      const rowIsActive = dataset === 'user_activity' && drilldownUserId && rowUserId === String(drilldownUserId);
                      const rowHasLoadedDetail = rowIsActive && rowUserId === String(userDetail?.user?.user_id || '');

                      const baseRow = (
                        <tr key={`row-${i}`}>
                          {dataset === 'user_activity' && (
                            <td data-label="History">
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ padding: '2px 8px', fontSize: 12 }}
                                onClick={() => loadUserActivityHistory(r)}
                              >
                                {rowIsActive ? (userDetailLoading ? 'Loading…' : 'Refresh') : 'View'}
                              </button>
                            </td>
                          )}
                          {preview.headers.map((h, hi) => (
                            <td
                              key={h}
                              className={hi === 0 ? 'card-title' : undefined}
                              data-label={h.replace(/_/g, ' ')}
                            >
                              {renderCell(h, r[h], r)}
                            </td>
                          ))}
                        </tr>
                      );

                      if (!rowIsActive) return [baseRow];

                      return [
                        baseRow,
                        <tr key={`drill-${i}`} style={{ background: 'var(--bg-sunken)' }}>
                          <td colSpan={preview.headers.length + 1} style={{ padding: 0 }}>
                            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-subtle)' }}>
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--fg-1)' }}>User Drill-down</div>
                                <div style={{ fontSize: 12.5, color: 'var(--fg-3)', marginTop: 2 }}>Login and movement history for selected user</div>
                              </div>
                              {userDetailLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--fg-3)', fontSize: 13 }}>
                                  <RefreshCw size={14} className="spin" />
                                  Loading user history…
                                </div>
                              ) : userDetailError ? (
                                <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>{userDetailError}</div>
                              ) : rowHasLoadedDetail && userDetail ? (
                                <>
                                  <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{userDetail.user?.full_name || userDetail.user?.username || userDetail.user?.email}</div>
                                      <div style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>{userDetail.user?.email || userDetail.user?.username}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 16, fontSize: 12.5, color: 'var(--fg-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                                      <span>Total events: {userDetail.summary?.total_events_count ?? 0}</span>
                                      <span>Login events: {userDetail.summary?.login_event_count ?? 0}</span>
                                      <span>Movement: {userDetail.summary?.movement_count ?? 0}</span>
                                      <span>Last activity: {formatDateTime(userDetail.summary?.last_activity_in_range)}</span>
                                    </div>
                                  </div>
                                  <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                    <table className="tbl">
                                      <thead>
                                        <tr>
                                          <th>Timestamp</th>
                                          <th>Operation</th>
                                          <th>Table</th>
                                          <th>Primary Key</th>
                                          <th>Unit</th>
                                          <th>Changed By</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(userDetail.rows || []).map((row, idx) => (
                                          <tr key={`${row.timestamp || 'ts'}-${row.operation || 'op'}-${idx}`}>
                                            <td className="card-title" data-label="Timestamp">{formatDateTime(row.timestamp)}</td>
                                            <td data-label="Operation">{row.operation || '-'}</td>
                                            <td data-label="Table">{row.table_name || '-'}</td>
                                            <td data-label="Primary Key">{row.primary_key || '-'}</td>
                                            <td data-label="Unit">{row.unit_id || '-'}</td>
                                            <td data-label="Changed By">{row.changed_by || '-'}</td>
                                          </tr>
                                        ))}
                                        {(!userDetail.rows || userDetail.rows.length === 0) && (
                                          <tr>
                                            <td colSpan={6} style={{ textAlign: 'center', color: 'var(--fg-3)', fontSize: 12.5 }}>No activity found in the selected date range.</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              ) : (
                                <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>
                                  Click <strong>View</strong> to load this user&apos;s activity history.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ];
                    })}
                  </tbody>
                </table>
              </div>
              {preview.rows.length > 50 && (
                <div style={{ padding: '10px 16px', fontSize: 12.5, color: 'var(--fg-3)', borderTop: '1px solid var(--border-subtle)' }}>
                  Showing first 50 of {preview.rows.length} rows
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--fg-3)' }}>
              <FileText size={22} style={{ display: 'block', margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontSize: 13.5 }}>Select a report and click Preview</p>
            </div>
          )}
        </div>

      </div>
    </>
  );
}
